/**
 * Iter235 — Repository Recovery View
 * Dedicated tab that lists prospects auto-archived by the enrichment pipeline
 * (archived_reason="no_email_found"). Each row can be unarchived with a
 * single click; the backend also fires a fresh _do_enrich pass so operators
 * see whether recent API-coverage improvements can recover the email.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Archive, RefreshCw, Loader2, MapPin, DollarSign, Search,
  ArrowLeft, RotateCcw, CheckCircle2, XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (v) => v == null ? "—" : `$${(v / 1000).toFixed(0)}k`;
const REASON_LABEL = {
  no_email_found: "Sem e-mail encontrado (3 tentativas)",
};

export default function SmithRepository() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("no_email_found");
  const [retrying, setRetrying] = useState(new Set());
  // Iter237 · Bulk retry state + polling.
  const [bulkJob, setBulkJob] = useState(null);      // {job_id, total, processed, phase, ...}
  const [bulkStarting, setBulkStarting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100", offset: "0" });
      if (reason) params.set("reason", reason);
      const { data } = await axios.get(
        `${API}/smith/find-email/archived?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao carregar repositório");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reason]);

  // Iter237 · Poll bulk job progress until it finishes, then refresh table.
  useEffect(() => {
    if (!bulkJob?.job_id || bulkJob.phase === "done") return undefined;
    const tick = async () => {
      try {
        const { data } = await axios.get(
          `${API}/smith/find-email/sweep-status/${bulkJob.job_id}`);
        setBulkJob((cur) => ({ ...(cur || {}), ...data }));
        if (data.phase === "done") {
          toast.success(
            `Bulk retry finalizado · ${data.recovered} recuperado(s) · ${data.archived} re-arquivado(s)`,
            { duration: 8000 });
          load();
        }
      } catch { /* transient */ }
    };
    const i = setInterval(tick, 4000);
    tick();
    return () => clearInterval(i);
  }, [bulkJob?.job_id, bulkJob?.phase]);   // eslint-disable-line react-hooks/exhaustive-deps

  const retryAll = async () => {
    if (!confirm(`Rodar retry em TODOS os ${total} lead(s) arquivados? Pode levar 15-30 min.`)) return;
    setBulkStarting(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/find-email/unarchive-all`,
        { reason, retry_enrich: true });
      if (!data.job_id) {
        toast.info(data.message || "Nada para reprocessar");
        return;
      }
      if (data.already_running) {
        toast.info("Já existe um sweep rodando — mostrando progresso");
      } else {
        toast.success(`Bulk retry iniciado em ${data.unarchived} lead(s)`);
      }
      setBulkJob({ job_id: data.job_id, total: data.total_targets || data.unarchived,
                     processed: 0, phase: "running" });
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao iniciar bulk retry");
    } finally { setBulkStarting(false); }
  };

  const retryOne = async (pid) => {
    setRetrying((cur) => { const n = new Set(cur); n.add(pid); return n; });
    try {
      const { data } = await axios.post(
        `${API}/smith/find-email/unarchive/${pid}`,
        { retry_enrich: true });
      if (data?.emails_found > 0) {
        toast.success(`✅ Recuperado! ${data.emails_found} e-mail(s) encontrado(s)`);
        setItems((cur) => cur.filter((p) => p.id !== pid));
        setTotal((t) => Math.max(0, t - 1));
      } else {
        toast.info("Sem e-mail encontrado — voltará ao repositório se falhar 3× seguidas");
      }
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao tentar recuperar");
    } finally {
      setRetrying((cur) => { const n = new Set(cur); n.delete(pid); return n; });
    }
  };

  return (
    <div className="min-h-screen text-white"
         style={{ background: "linear-gradient(180deg,#0F172A 0%,#000 100%)" }}
         data-testid="smith-repository-page">
      <div className="max-w-7xl mx-auto p-4 lg:p-6 space-y-4">
        <header className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/smith/prospects"
                  data-testid="repo-back-btn"
                  className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-[12px]">
              <ArrowLeft className="w-3.5 h-3.5" /> Prospects
            </Link>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#F59E0B 0%,#78350F 100%)" }}>
              <Archive className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-[20px] font-extrabold">Repositório de Recuperação</h1>
              <p className="text-[11.5px] text-slate-400">
                Leads arquivados por 3+ tentativas de enrichment sem sucesso · sistema roda automaticamente toda madrugada
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={load}
              data-testid="repo-refresh-btn"
              disabled={loading}
              className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            {/* Iter237 · Bulk retry ALL archived leads. Handy after a new
                Apollo/Hunter key gets provisioned. */}
            <button type="button" onClick={retryAll}
              data-testid="repo-retry-all-btn"
              disabled={bulkStarting || total === 0 || (bulkJob?.phase === "running")}
              title="Reprocessa TODOS os leads arquivados de uma vez (dispara enrichment em background)"
              className="h-9 px-3 rounded-lg text-[12.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
              {bulkStarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              Retry todos ({total})
            </button>
          </div>
        </header>

        {/* Iter237 · Progress banner while bulk retry job runs */}
        {bulkJob && bulkJob.job_id && bulkJob.phase !== "done" && (
          <div className="rounded-2xl border p-3 flex items-center gap-3"
               data-testid="repo-bulk-progress"
               style={{ background: "linear-gradient(135deg,rgba(14,165,233,0.15) 0%,rgba(15,23,42,0.7) 60%)",
                        borderColor: "rgba(14,165,233,0.45)" }}>
            <Loader2 className="w-4 h-4 animate-spin text-sky-300" />
            <div className="flex-1 min-w-[180px]">
              <div className="h-2 bg-black/40 rounded-full overflow-hidden">
                <div className="h-full bg-sky-500 transition-all"
                     style={{ width: `${bulkJob.total ? (bulkJob.processed || 0) * 100 / bulkJob.total : 0}%` }} />
              </div>
              <div className="text-[10.5px] text-slate-300 mt-1 tabular-nums">
                Bulk retry: {bulkJob.processed || 0} / {bulkJob.total || 0} ·
                recuperados <b className="text-emerald-300">{bulkJob.recovered || 0}</b> ·
                re-arquivados <b className="text-amber-300">{bulkJob.archived || 0}</b>
              </div>
            </div>
          </div>
        )}

        {/* Summary strip */}
        <div className="rounded-2xl border p-3 flex items-center gap-3"
             data-testid="repo-summary"
             style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(245,158,11,0.3)" }}>
          <Archive className="w-4 h-4 text-amber-400" />
          <div className="text-[12.5px]">
            <b className="text-amber-200 tabular-nums text-[15px]">{total}</b>{" "}
            <span className="text-slate-400">lead(s) no repositório</span>
            <span className="mx-2 text-slate-600">·</span>
            <span className="text-slate-500">
              Motivo: <b className="text-slate-300">{REASON_LABEL[reason] || reason}</b>
            </span>
          </div>
          <select value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="repo-reason-select"
                    className="ml-auto bg-black/40 border border-white/10 rounded px-2 py-1 text-[11.5px]">
            <option value="no_email_found">Sem e-mail (padrão)</option>
            <option value="">Todos os motivos</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="p-10 text-center text-slate-400" data-testid="repo-loading">
            <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Carregando repositório…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border p-10 text-center"
               data-testid="repo-empty"
               style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(16,185,129,0.3)" }}>
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <div className="text-[14px] font-bold text-white">Repositório vazio</div>
            <p className="text-[12px] text-slate-400 mt-1">
              Todos os leads da base atualmente têm e-mail resolvido ou estão em tentativa ativa.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border overflow-hidden"
               style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(245,158,11,0.2)" }}>
            <table className="w-full text-[12.5px]" data-testid="repo-table">
              <thead>
                <tr className="text-slate-400 text-[10.5px] uppercase tracking-widest bg-white/5">
                  <th className="text-left px-4 py-2.5 font-bold">Empresa</th>
                  <th className="text-left px-2 py-2.5 font-bold">Segmento</th>
                  <th className="text-left px-2 py-2.5 font-bold">Cidade</th>
                  <th className="text-right px-2 py-2.5 font-bold">Score</th>
                  <th className="text-right px-2 py-2.5 font-bold">ACV</th>
                  <th className="text-right px-2 py-2.5 font-bold">Tentativas</th>
                  <th className="text-left px-2 py-2.5 font-bold">Arquivado em</th>
                  <th className="text-right px-4 py-2.5 font-bold">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                {items.map((p) => {
                  const isRetrying = retrying.has(p.id);
                  return (
                    <tr key={p.id}
                        data-testid={`repo-row-${p.id}`}
                        className="hover:bg-white/5">
                      <td className="px-4 py-2">
                        <Link to={`/smith/prospects/${p.id}`}
                              className="font-bold text-white hover:text-[#DC2626]"
                              data-testid={`repo-company-${p.id}`}>
                          {p.company?.name || "—"}
                        </Link>
                      </td>
                      <td className="px-2 py-2 capitalize text-slate-300">
                        {(p.segment || "").replace(/_/g, " ")}
                      </td>
                      <td className="px-2 py-2 text-slate-300">
                        <MapPin className="w-3 h-3 inline text-slate-500 mr-1" />
                        {p.property?.city}, {p.property?.state}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-bold text-white">
                        <span className="mr-1">{p.tier?.icon}</span>
                        {p.opportunity_score}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-bold text-emerald-400">
                        {usd(p.opportunity?.acv_est)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-300">
                          {p.email_unresolvable_attempts || 0}× falhas
                        </span>
                      </td>
                      <td className="px-2 py-2 text-slate-400 text-[11px]">
                        {p.archived_at ? new Date(p.archived_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button type="button"
                          onClick={() => retryOne(p.id)}
                          disabled={isRetrying}
                          data-testid={`repo-retry-${p.id}`}
                          className="h-7 px-2 rounded text-[11px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-50"
                          style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
                          {isRetrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                          {isRetrying ? "Buscando…" : "Tentar de novo"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
